import { type Quaternion, Vector3 } from 'three/webgpu';

const _u = new Vector3();
const _w = new Vector3();
const _Y = new Vector3(0, 1, 0);

/**
 * Two-bone reach: the elbow (or knee) for a limb from `root` to `end` with bones `upper` and
 * `lower` long, bending toward `pole`. An end out of reach moves the root toward it (a lunge:
 * the shoulder follows the hand), so the hand always arrives; `root` is updated in place.
 */
export function solveLimb(
  root: Vector3,
  end: Vector3,
  upper: number,
  lower: number,
  pole: Vector3,
  elbow: Vector3,
): Vector3 {
  const reach = (upper + lower) * 0.995;
  _u.subVectors(end, root);
  let d = _u.length();
  if (d > reach) {
    root.addScaledVector(_u, (d - reach) / d);
    _u.subVectors(end, root);
    d = reach;
  }
  d = Math.max(d, Math.abs(upper - lower) + 1e-3);
  _u.normalize();
  // Bend toward the pole: its direction across the root → end line.
  _w.subVectors(pole, root);
  _w.addScaledVector(_u, -_w.dot(_u));
  if (_w.lengthSq() < 1e-9) _w.set(0, -1, 0).addScaledVector(_u, _u.y);
  _w.normalize();
  const along = (upper * upper + d * d - lower * lower) / (2 * d);
  const across = Math.sqrt(Math.max(0, upper * upper - along * along));
  return elbow.copy(root).addScaledVector(_u, along).addScaledVector(_w, across);
}

/** Turn and stretch a bone mesh (a unit-long piece along +Y from its origin) to run from a to b. */
export function placeBone(
  mesh: { position: Vector3; quaternion: Quaternion; scale: Vector3 },
  a: Vector3,
  b: Vector3,
): void {
  _u.subVectors(b, a);
  const len = _u.length();
  mesh.position.copy(a);
  mesh.quaternion.setFromUnitVectors(_Y, len > 1e-9 ? _u.divideScalar(len) : _Y);
  mesh.scale.set(1, Math.max(len, 1e-4), 1);
}
